import { tw } from "@careinfo/tailwind";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import useGetHook from "../../../customHooks/useGetHook";
import { downloadApiData } from "../../../helpers/AxiosInstance";
import CreateIcon from "../../../Icons/CreateIcon";
//@ts-ignore
import { TableSettingsLayout } from "@careinfo/layout";
import DownloadIcon from "../../../Icons/DownloadIcon";
import { classNames } from "../../../utils/classNames";
import Template from "./loading";

interface IDataTable {
  url: string;
  queryKey: string;
  columns: any;
  addNewPagePath?: string;
  tableName?: string;
  params?: any;
  scanComponent?: any;
  actionMenu?: any;
  orderSearch?: boolean;
  setRerender?: any;
  subOrderSearch?: boolean;
  isUpload?: boolean;
  isDownload?: boolean;
  handleDetailNavigate?: (row: any) => void;
  info?: React.ReactElement;
}
const isAfter24HoursFromModified = (modified?: string) => {
  if (!modified) return false;
  return moment().diff(moment(modified), "hours") >= 24;
};

function DataTable(props: IDataTable) {
  const {
    url,
    queryKey,
    columns,
    addNewPagePath,
    scanComponent,
    actionMenu,
    params: otherParams,
    isUpload = false,
    orderSearch = false,
    subOrderSearch = false,
    isDownload = false,
    handleDetailNavigate,
    tableName,
    info,
  } = props;
  const [searchParams, setSearchParams] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });
  const [limit, setLimit] = useState(10);
  const page = parseInt(searchParams.get("page") ?? "0");
  const searchQuery = searchParams.get("searchQuery");
  const debouncesearch = useDebounce(searchQuery);
  const selectedItems = searchParams.get("selectedItems");
  const navigate = useNavigate();
  const [isactive, setISactive] = useState("manifest");
  const [isOpenTableSetting, setIsOpenTableSettings] = useState(false);
  const [countSelectedItems, setSelectedItems] = useState(0);
  const [isorder, setISOrder] = useState("consignment");
  useEffect(() => {
    const items = selectedItems?.split(",");
    setSelectedItems(items?.length);
  }, [selectedItems]);

  const { data: serverData, isLoading } = useGetHook({
    queryKey: [
      queryKey,
      `${queryKey}-${limit}-${page}-${debouncesearch}-${isactive}-${isorder}-${JSON.stringify(
        otherParams,
      )}`,
    ],
    url: url,
    params: {
      limit: limit,
      offset: limit * page,
      ...(isactive === "order"
        ? { order: debouncesearch }
        : isorder === "sub-order"
        ? { parent__isnull: false }
        : {
            parent__isnull: true,
            search: debouncesearch,
          }),
      ...otherParams,
    },
  });

  const [data, setData] = useState([]);

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      setData([...serverData?.results]);
      const clonedSearchParams = new URLSearchParams(searchParams);
      if (clonedSearchParams.get("selectAllItems") === "1") {
        let _newItems = serverData?.results
          ?.map((item) => item.id)
          .join(",")
          .toString();
        clonedSearchParams.set("page", String(page));
        clonedSearchParams.set("selectedItems", _newItems);
        clonedSearchParams.set(
          "selectAllItems",
          clonedSearchParams.get("selectAllItems"),
        );
        clonedSearchParams.set("searchQuery", searchQuery);
        setSearchParams(clonedSearchParams);
      }
    }
  }, [serverData, page, searchParams, searchQuery, setSearchParams]);

  // Create the table and pass your options
  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleAddPageRoute = () => {
    navigate(addNewPagePath);
  };

  useEffect(() => {
    table.setPageSize(limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  const handleNextPage = () => {
    searchParams.set("page", String(page + 1));
    setSearchParams(searchParams);
  };

  const handlePreviousPage = () => {
    searchParams.set("page", String(page - 1));
    setSearchParams(searchParams);
  };

  const handleLastpage = () => {
    searchParams.set("page", String(Math.floor(serverData?.count / limit)));
    setSearchParams(searchParams);
  };

  const handleFirstPage = () => {
    searchParams.set("page", String(0));
    setSearchParams(searchParams);
  };

  const handleSearch = (value) => {
    searchParams.set("searchQuery", value);
    searchParams.set("page", String(0));
    setSearchParams(searchParams);
  };
  const handleDownloadExcel = async () => {
    try {
      await downloadApiData(
        `/manifestservice/api/manifest/excel-download/`,
        `${queryKey}-${moment().format("MMMM-Do-YYYY-h-mm-ss")}.xlsx`,
        selectedItems,
      );
    } catch (e) {
      console.error(e);
    }
  };

  return isLoading ? (
    <Template addNewPath={addNewPagePath} />
  ) : (
    <>
      <div className={tw`w-full flex justify-between -mt-2`}>
        {addNewPagePath && (
          <div>
            <button
              onClick={handleAddPageRoute}
              className={tw`p-2 flex gap-x-2 bg-primary-light my-2 rounded text-primary`}
            >
              <CreateIcon color={"var(--primary)"} />
              Add New
            </button>
            {scanComponent}
          </div>
        )}
      </div>
      {info && <div>{info}</div>}
      <div
        className={tw`w-full bg-white rounded-sm mb-8`}
        style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
      >
        <div
          className={tw`w-full grid grid-cols-1 gap-4 md:flex justify-between items-center p-2`}
        >
          <div>
            <input
              type="text"
              placeholder="Search"
              value={searchQuery ?? ""}
              onChange={(e) => handleSearch(e?.target?.value)}
              className={tw`border w-full border-1 border-primary focus:outline-none p-1`}
            />
          </div>
          {orderSearch && (
            <div className={tw`flex  border mr-2   rounded-full `}>
              <button
                onClick={() => setISactive("manifest")}
                className={classNames(
                  isactive === "manifest"
                    ? tw`bg-primary text-white px-3`
                    : tw`px-2`,
                  tw`focus:outline-none  rounded-full whitespace-nowrap `,
                )}
              >
                Manifest ID
              </button>
              <button
                onClick={() => setISactive("order")}
                className={classNames(
                  isactive === "order"
                    ? tw`bg-primary text-white  px-3`
                    : tw`px-2`,
                  tw`focus:outline-none px-2 rounded-full whitespace-nowrap py-2`,
                )}
              >
                Order ID
              </button>
            </div>
          )}
          {subOrderSearch && (
            <div className={tw`flex  border mr-2   rounded-full `}>
              <button
                onClick={() => setISOrder("consignment")}
                className={classNames(
                  isorder === "consignment"
                    ? tw`bg-primary text-white px-3`
                    : tw`px-2`,
                  tw`focus:outline-none  rounded-full whitespace-nowrap `,
                )}
              >
                Consignment
              </button>
              <button
                onClick={() => setISOrder("sub-order")}
                className={classNames(
                  isorder === "sub-order"
                    ? tw`bg-primary text-white px-3`
                    : tw`px-3`,
                  tw`focus:outline-none px-2 rounded-full whitespace-nowrap py-2`,
                )}
              >
                Parcel
              </button>
            </div>
          )}
          <div
            className={tw`text-primary w-full items-center justify-end flex gap-x-2`}
          >
            {countSelectedItems ? (
              <>
                <div
                  className={tw`text-primary gap-x-2 flex items-center justify-end `}
                >
                  <span>Selected:{countSelectedItems ?? 0}</span>
                  <button
                    className={tw`focus:outline-none px-3 py-[3px] border-1 border-primary rounded-md flex items-center justify-center`}
                    onClick={() => {
                      const clonedParams = new URLSearchParams(searchParams);
                      clonedParams.delete("selectAllItems");
                      clonedParams.delete("selectedItems");
                      clonedParams.delete("selectedOrders");
                      setSearchParams(clonedParams);
                    }}
                  >
                    Unselect
                  </button>
                </div>
              </>
            ) : null}
            {isDownload ? (
              <button
                type="button"
                className={tw`flex gap-x-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={handleDownloadExcel}
              >
                <DownloadIcon color={"var(--primary)"} />
                Download
              </button>
            ) : (
              ""
            )}
            <span className={tw` cursor-pointer`}>{actionMenu}</span>
          </div>
          <TableSettingsLayout
            isOpenTableSetting={isOpenTableSetting}
            setIsOpenTableSettings={setIsOpenTableSettings}
            tableName={tableName}
            defaultColumns={columns}
            onSettingsLoaded={(settings) => {
              if (settings?.page_limit != null) {
                setLimit(settings.page_limit);
                searchParams.set("limit", settings.page_limit.toString());
                setSearchParams(searchParams);
              }
            }}
          />
        </div>
        <div className={tw`w-full overflow-auto z-0`}>
          <table className={tw`w-full`}>
            <thead className={tw`bg-primary text-white uppercase`}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className={tw``}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={classNames(
                        index === 0
                          ? tw``
                          : headerGroup.headers?.length - 1 === index
                          ? tw` text-left w-[8rem]`
                          : tw``,
                        tw`py-[5px] text-left px-5 whitespace-nowrap`,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  onClick={() => handleDetailNavigate(row)}
                  key={row.id}
                  className={classNames(
                    tw`hover:transition-all cursor-pointer`,

                    row?.original?.is_active === false &&
                      row?.original?.items?.total !==
                        row?.original?.items?.received &&
                      isAfter24HoursFromModified(row?.original?.modified)
                      ? tw`bg-red-100 hover:bg-red-200`
                      : row?.original?.is_active === false
                      ? tw`bg-green-100 hover:bg-green-200`
                      : tw`even:bg-gray-50 odd:bg-white hover:bg-gray-100`,
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={tw`py-[10px] text-left px-5`}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
        </div>
        <div
          className={tw`flex bg-white py-2 my-4 w-full justify-center gap-4`}
        >
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleFirstPage()}
            disabled={!serverData?.previous}
          >
            {"<<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handlePreviousPage()}
            disabled={!serverData?.previous}
          >
            {"<"}
          </button>
          <button
            className={tw`border rounded p-1`}
            onClick={() => handleNextPage()}
            disabled={!serverData?.next}
          >
            {">"}
          </button>
          <button
            className={tw`border hidden md:block rounded p-1`}
            onClick={() => handleLastpage()}
            disabled={!serverData?.next}
          >
            {">>"}
          </button>
          <span className={tw`flex items-center gap-1`}>
            <div>Page</div>
            <strong>
              {page + 1} of {Math.ceil(serverData?.count / limit)}
            </strong>
          </span>
          <select
            value={table?.getState()?.pagination?.pageSize}
            onChange={(e) => {
              setLimit(Number(e?.target?.value));
              searchParams.set("limit", e?.target?.value);
              setSearchParams(searchParams);
            }}
          >
            {[10, 50, 100, 200, 500]?.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <p className={tw`mt-1`}>All: {serverData?.count}</p>
        </div>
      </div>
    </>
  );
}

export default DataTable;
